import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { BOAT_TYPES, loadBoatTypes, getBoatTypeLayout } from '../../utils/boatTypes';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useMobileCrewOperations } from '../../hooks/useMobileCrewOperations';
import { useMobile } from '../../hooks/useMobile';
import { getRiggingValidationWarnings, canAssignRowerToSeat } from '../../utils/riggingValidation';
import { SeatSlot } from './'; // Changed PositionSlot to SeatSlot
import { RowerCard } from './';

const LineupEditor = ({ selectedCrew, setSelectedCrew, crews, setCrews, availableRowers = [] }) => {
  const { isMobile } = useMobile();
  const [boatTypesLoaded, setBoatTypesLoaded] = useState(false);

  // Load boat types if not already loaded
  useEffect(() => {
    if (!boatTypesLoaded && Object.keys(BOAT_TYPES).length === 0) {
      loadBoatTypes().then(() => {
        setBoatTypesLoaded(true);
      });
    } else {
      setBoatTypesLoaded(true);
    }
  }, [boatTypesLoaded]);

  // Use the drag and drop hook for desktop
  const {
    dropTarget,
    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleRemoveMember, // Changed handleRemoveFromPosition to handleRemoveMember
  } = useDragAndDrop(selectedCrew, setSelectedCrew, crews, setCrews);

  // Use mobile operations for mobile devices
  const {
    handleAssignRowerToSeat,
    handleRemoveMember: handleMobileRemove,
    handleChangeSeatAssignment,
  } = useMobileCrewOperations(selectedCrew, setSelectedCrew, crews, setCrews);

  // Mobile handler for experimental crew dropdown
  const handleExperimentalCrewAssign = (e) => {
    const selectedRowerId = e.target.value;
    if (selectedRowerId && isMobile) {
      handleAssignRowerToSeat(parseInt(selectedRowerId), null); // No specific seat for experimental crews
    }
  };

  // Helper function to get side preference styling
  const getSideIndicatorStyle = (side) => {
    switch (side) {
      case 'port':
        return 'bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs font-medium';
      case 'starboard':
        return 'bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-medium';
      case 'both':
        return 'bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-medium';
      case 'none':
      default:
        return 'bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full text-xs font-medium';
    }
  };

  // Get available rowers excluding already assigned ones
  const getAvailableRowersForCrew = () => {
    if (!availableRowers || !selectedCrew) return [];

    const assignedUserIds = selectedCrew.members?.map((member) => member.user?.id) || [];
    return availableRowers.filter((rower) => !assignedUserIds.includes(rower.id));
  };

  const availableForAssignment = getAvailableRowersForCrew();

  // Convert crew members to lineup array format for rigging validation
  const getLineupArrayForValidation = () => {
    if (!selectedCrew || !selectedCrew.members) return [];

    const boatType = getBoatTypeLayout(selectedCrew.boat_type);

    const totalPositions = boatType.seats + (boatType.hasCox ? 1 : 0);
    const lineup = new Array(totalPositions).fill(null);

    // Fill the lineup array based on seat numbers
    selectedCrew.members.forEach((member) => {
      if (member.seat_number !== null && member.seat_number !== undefined) {
        // For boats with cox: seat 0 = index 0, seat 1 = index 1, etc.
        // For boats without cox: seat 1 = index 0, seat 2 = index 1, etc.
        const arrayIndex = boatType.hasCox ? member.seat_number : member.seat_number - 1;

        if (arrayIndex >= 0 && arrayIndex < totalPositions) {
          // Include user data for validation
          lineup[arrayIndex] = {
            ...member.user,
            preferred_side: member.user?.preferred_side || member.user?.side_preference,
            first_name: member.user?.first_name,
            last_name: member.user?.last_name,
          };
        }
      }
    });

    return lineup;
  };

  // Get rigging validation warnings
  const riggingWarnings = selectedCrew
    ? getRiggingValidationWarnings(selectedCrew, getLineupArrayForValidation(), {
        skipExperimentalCrews: true,
        enhanceWarningMessages: true,
      })
    : [];

  if (!selectedCrew) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg bg-gray-50">
        <p className="text-gray-500">Select a crew to view its lineup</p>
      </div>
    );
  }

  const boatType = getBoatTypeLayout(selectedCrew.boat_type);
  const { seats } = boatType;
  const totalSeats = seats;

  const renderSeats = () => {
    // For experimental crews, don't render seats
    if (selectedCrew.is_experimental) {
      return null;
    }

    const seatComponents = [];

    // Add cox position if boat has cox (seat 0)
    if (boatType.hasCox) {
      const coxInSeat = selectedCrew.members.find((m) => m.seat_number === 0);
      seatComponents.push(
        <SeatSlot
          key={0}
          seat_number={0}
          rowerInSeat={coxInSeat}
          isDropTarget={dropTarget === 0}
          onDragOver={!isMobile ? handleDragOver : undefined}
          onDragEnter={!isMobile ? handleDragEnter : undefined}
          onDragLeave={!isMobile ? handleDragLeave : undefined}
          onDrop={!isMobile ? handleDrop : undefined}
          onRemoveRower={isMobile ? handleMobileRemove : handleRemoveMember}
          onDragStart={!isMobile ? handleDragStart : undefined}
          // Mobile-specific props
          availableRowers={isMobile ? availableForAssignment : []}
          onMobileAssign={isMobile ? handleAssignRowerToSeat : undefined}
          onMobileChangeSeat={isMobile ? handleChangeSeatAssignment : undefined}
          crew={selectedCrew}
        />
      );
    }

    // Add regular rower seats (1 to totalSeats)
    for (let i = 1; i <= totalSeats; i++) {
      const rowerInSeat = selectedCrew.members.find((m) => m.seat_number === i);
      seatComponents.push(
        <SeatSlot
          key={i}
          seat_number={i}
          rowerInSeat={rowerInSeat}
          isDropTarget={dropTarget === i}
          onDragOver={!isMobile ? handleDragOver : undefined}
          onDragEnter={!isMobile ? handleDragEnter : undefined}
          onDragLeave={!isMobile ? handleDragLeave : undefined}
          onDrop={!isMobile ? handleDrop : undefined}
          onRemoveRower={isMobile ? handleMobileRemove : handleRemoveMember} // Changed onRemoveRower
          onDragStart={!isMobile ? handleDragStart : undefined}
          // Mobile-specific props
          availableRowers={isMobile ? availableForAssignment : []}
          onMobileAssign={isMobile ? handleAssignRowerToSeat : undefined}
          onMobileChangeSeat={isMobile ? handleChangeSeatAssignment : undefined}
          crew={selectedCrew}
        />
      );
    }
    return seatComponents;
  };

  const renderExperimentalCrew = () => {
    if (!selectedCrew.members || selectedCrew.members.length === 0) {
      return (
        <>
          <div
            className={`flex h-32 items-center justify-center rounded-lg border-2 border-dashed transition-all duration-200 ${
              dropTarget === 'crew-general'
                ? 'border-blue-500 bg-blue-50 text-blue-600'
                : 'border-gray-300 text-gray-400 hover:border-gray-400'
            }`}
          >
            <div className="text-center">
              <div className="text-sm">
                {dropTarget === 'crew-general' ? 'Drop rower here' : 'No crew members assigned'}
              </div>
              <div className="mt-1 text-xs">
                {dropTarget === 'crew-general'
                  ? 'Release to add to crew'
                  : 'This is an experimental crew'}
              </div>
            </div>
          </div>

          {/* Mobile: Add member dropdown for experimental crew */}
          {isMobile && availableForAssignment.length > 0 && (
            <div className="mt-4 space-y-2">
              <h5 className="text-sm font-medium text-gray-700">Add crew member:</h5>
              <select
                onChange={handleExperimentalCrewAssign}
                value=""
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Select rower to add...</option>
                {availableForAssignment.map((rower) => (
                  <option key={rower.id} value={rower.id}>
                    {rower.first_name} {rower.last_name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </>
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-gray-700">
            Crew Members ({selectedCrew.members.length})
          </h4>
          <div className="rounded-full bg-orange-100 px-3 py-1 text-sm text-orange-800">
            Experimental
          </div>
        </div>
        <div className="grid gap-3">
          {selectedCrew.members.map((member) => {
            const rowerPreferredSide = member.user?.preferred_side || member.user?.side_preference;

            return (
              <div
                key={member.id}
                className="flex items-center justify-between rounded-lg border bg-gray-50 p-3"
                draggable={!isMobile && !!handleDragStart}
                onDragStart={
                  !isMobile && handleDragStart ? (e) => handleDragStart(e, member.user) : undefined
                }
              >
                <div className="flex items-center">
                  <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-blue-500 text-sm font-semibold text-white">
                    {member.user?.first_name?.[0]}
                    {member.user?.last_name?.[0]}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">
                        {member.user?.first_name} {member.user?.last_name}
                      </div>
                      {/* Side preference indicator */}
                      {rowerPreferredSide && (
                        <span
                          className={`${getSideIndicatorStyle(rowerPreferredSide)}`}
                          title={`Prefers ${rowerPreferredSide} side`}
                        >
                          {rowerPreferredSide}
                        </span>
                      )}
                      {/* Strict preference indicator */}
                      {(member.user?.preferred_side_strict ||
                        member.user?.side_preference_strict) && (
                        <span className="text-xs text-orange-600" title="Strict side preference">
                          ⚡
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">{member.user?.email}</div>
                  </div>
                </div>
                <button
                  onClick={() =>
                    isMobile ? handleMobileRemove(member.id) : handleRemoveMember(member.id)
                  }
                  className="flex h-8 w-8 items-center justify-center rounded-full text-lg font-bold text-gray-400 transition-colors hover:text-red-500"
                  title="Remove from crew"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>

        {/* Mobile: Add member dropdown for experimental crew */}
        {isMobile && availableForAssignment.length > 0 && (
          <div className="mt-4 space-y-2">
            <h5 className="text-sm font-medium text-gray-700">Add crew member:</h5>
            <select
              onChange={handleExperimentalCrewAssign}
              value=""
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select rower to add...</option>
              {availableForAssignment.map((rower) => (
                <option key={rower.id} value={rower.id}>
                  {rower.first_name} {rower.last_name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col rounded-lg bg-white p-6 shadow-md">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-gray-800">{selectedCrew.name}</h3>
        <p className="text-sm text-gray-500">
          {boatType.name} ({selectedCrew.boat_type})
          {isMobile && <span className="ml-2 text-blue-600">(Mobile Mode)</span>}
        </p>
      </div>

      {selectedCrew.is_experimental ? (
        // Experimental crew: show members without seats, with general drop zone
        <div
          className={`flex-1 overflow-y-auto transition-all duration-200 ${
            dropTarget === 'crew-general' ? 'rounded-lg border-2 border-blue-300 bg-blue-50' : ''
          }`}
          onDragOver={!isMobile ? handleDragOver : undefined}
          onDrop={!isMobile ? (e) => handleDrop(e, 'crew-general') : undefined}
          onDragEnter={!isMobile ? (e) => handleDragEnter(e, 'crew-general') : undefined}
          onDragLeave={!isMobile ? handleDragLeave : undefined}
        >
          {renderExperimentalCrew()}
        </div>
      ) : (
        // Regular crew: show seat assignments
        <div
          className="flex flex-1 flex-col gap-4 overflow-y-auto"
          onDragOver={!isMobile ? handleDragOver : undefined}
          onDrop={!isMobile ? (e) => handleDrop(e, 'crew-general') : undefined}
          onDragEnter={!isMobile ? (e) => handleDragEnter(e, 'crew-general') : undefined}
          onDragLeave={!isMobile ? handleDragLeave : undefined}
        >
          {renderSeats()}

          {/* Rigging Validation Warnings */}
          {riggingWarnings.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="text-sm font-medium text-amber-700">⚠️ Side Preference Warnings</h4>
              <p className="mb-3 text-xs text-amber-600">
                These warnings help optimize crew performance. To resolve side preference
                mismatches, update rowers' preferred sides in their profile settings.
              </p>
              {riggingWarnings.map((warning, index) => (
                <div key={index} className="rounded-md border border-amber-200 bg-amber-50 p-3">
                  <div className="text-sm text-amber-800">
                    <span className="font-medium">Seat {warning.seatNumber}:</span>{' '}
                    {warning.rowerName}
                  </div>
                  <div className="mt-1 text-xs text-amber-700">{warning.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

LineupEditor.propTypes = {
  selectedCrew: PropTypes.object,
  setSelectedCrew: PropTypes.func.isRequired,
  crews: PropTypes.array.isRequired,
  setCrews: PropTypes.func.isRequired,
  availableRowers: PropTypes.array,
};

export default LineupEditor;
